import { NoteEditorSourceBarChip } from "@/components/note/editor/source-bar/NoteEditorSourceBarChip";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { INoteObservable } from "@/store/note/types";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FadeHorizontalScroll } from "@/components/horizontal-scroll/FadeHorizontalScroll";
import { useRef } from "react";
import { generateRecentDateString } from "@/domains/date/date";
import { MDSText, MDSTextOverflow, MDSTextVariant, MDSTooltip, MDSTooltipPlacement } from "@/mds";
import { mdsPanelWidths } from "@/design-system/foundations";

interface NoteEditorBottomBarProps {
  note: INoteObservable;
  tooltipLabel: string;
  containerWidth?: number;
}

export const NoteEditorBottomBar = observer<NoteEditorBottomBarProps>(
  ({ note, tooltipLabel, containerWidth }) => {
    const sources = note.sourceList?.allSupportedSources ?? [];

    const playingSourceIdRef = useRef<string | null>(null);

    const collapsedChips =
      typeof containerWidth === "number" && containerWidth <= mdsPanelWidths().narrow;

    const hasSuggestion = !!note?.suggestionData;

    return (
      <Container $hasSuggestion={hasSuggestion}>
        <ScrollableArea>
          <FadeHorizontalScroll>
            <InnerContainer>
              {note.isShared && <MdsIcon kind={MdsIconKind.Shared} className="shared-icon" />}
              {!note.isShared && <MdsIcon kind={MdsIconKind.LockSolid} className="lock-icon" />}
              <MDSTooltip
                config={{
                  label: tooltipLabel,
                  placement: MDSTooltipPlacement.Top,
                }}
              >
                <MDSText
                  variant={MDSTextVariant.TextXs}
                  numberOfLines={1}
                  overflow={MDSTextOverflow.Ellipsis}
                >
                  {note.isShared && "Shared"}
                  {!note.isShared && "Private"}
                  {" • "}
                  Edited {generateRecentDateString(note.modifiedAt).toLowerCase()} by{" "}
                  {note.authors?.[note.authors.length - 1]?.profileDisplayName}
                </MDSText>
              </MDSTooltip>
              <Spacer />
              {sources.length > 0 && (
                <MdsIcon kind={MdsIconKind.Paperclip} className="paperclip-icon" />
              )}
              {sources.map(source => (
                <NoteEditorSourceBarChip
                  isCollapsed={collapsedChips}
                  key={source.id}
                  source={source}
                  note={note}
                  playingSourceIdRef={playingSourceIdRef}
                />
              ))}
            </InnerContainer>
          </FadeHorizontalScroll>
        </ScrollableArea>
      </Container>
    );
  }
);
NoteEditorBottomBar.displayName = "NoteEditorBottomBar";

const Container = styled.div<{ $hasSuggestion: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: ${({ theme }) =>
    `${theme.spacing["3"]} ${theme.spacing["3"]} ${theme.spacing["3"]} ${theme.spacing["7"]}`};
  background-color: ${({ theme, $hasSuggestion }) =>
    $hasSuggestion ? theme.colors.background.diff : theme.colors.surface.pane};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-top: 1px solid ${({ theme }) => theme.colors.surface.chrome};

  & .shared-icon > .mds-icon {
    font-size: 10px;
    width: 10px;
    height: 10px;
    color: ${({ theme }) => theme.colors.icon.secondary};
  }

  & .paperclip-icon > .mds-icon {
    font-size: 12px;
    width: 12px;
    height: 12px;
    color: ${({ theme }) => theme.colors.icon.secondary};
  }
`;

const ScrollableArea = styled.div`
  flex-grow: 1;
  /* Hide the native scrollbar, FadeHorizontalScroll provides its own */
  overflow: hidden;
`;

const Spacer = styled.div`
  flex: 1;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  min-width: 100%;
`;
